import { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { Activity, CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import { useJobProgressStream, type ProgressEvent } from './useJobProgressStream';

/**
 * Live activity feed for a reconciliation job.
 *
 * Subscribes to /api/recon/jobs/:id/events via EventSource (SSE) and renders
 * a chronological list of pipeline events: portfolio parse, DP extraction
 * (with token counts per Claude call), match engine, classifier per row,
 * persistence, completion.
 *
 * Auto-scrolls to the latest event. Cleans up the EventSource on unmount.
 * EventSource auto-reconnects on transient disconnects; we listen for
 * an explicit `end` event to close.
 *
 * The session cookie is sent automatically (EventSource uses the cookie
 * jar with withCredentials). We do NOT need a separate auth handshake.
 */

const LEVEL_STYLES = {
  info:    { icon: Loader2,       color: 'text-ink-400',      bg: 'pill-neutral' },
  success: { icon: CheckCircle2,  color: 'text-emerald-500',  bg: 'bg-emerald-500/[0.08]' },
  warn:    { icon: AlertTriangle, color: 'text-amber-500',    bg: 'bg-amber-500/[0.08]' },
  error:   { icon: XCircle,       color: 'text-rose-500',     bg: 'bg-rose-500/[0.10]' },
} as const;

/** Format USD amount. Sub-dollar gets 4 decimals; ≥$1 gets 2. */
function formatUsd(amount: number): string {
  if (amount < 1) return `$${amount.toFixed(4)}`;
  return `$${amount.toFixed(2)}`;
}

/** Sum cost across all claude.call events we've seen. */
function sumCost(events: ProgressEvent[]): { total: number; calls: number } {
  let total = 0;
  let calls = 0;
  for (const e of events) {
    if (e.phase === 'claude.call' && e.level === 'success' && typeof e.detail?.cost_usd === 'number') {
      total += e.detail.cost_usd as number;
      calls++;
    }
  }
  return { total, calls };
}


export function LiveActivityFeed({
  jobId,
  apiBaseUrl,
  onTerminal,
}: {
  jobId: string;
  apiBaseUrl: string;
  /** Called when the worker emits job.complete or job.failed. Lets the parent refetch the job. */
  onTerminal?: () => void;
}) {
  const { events, connected, done } = useJobProgressStream(jobId, apiBaseUrl, onTerminal);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll on new events
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events.length]);

  // Find the most recent 'step.current' event. The worker emits these at
  // each major phase transition (portfolio parse, DP doc 1, classifier
  // batch, etc.). We pin it at the top of the feed as a persistent header
  // so users always know what's happening RIGHT NOW, separate from the
  // scrolling history.
  const currentStep = (() => {
    for (let i = events.length - 1; i >= 0; i--) {
      if (events[i].phase === 'step.current') return events[i];
    }
    return null;
  })();
  // Hide step.current events from the scrolling list — they're displayed
  // by the pinned header instead, so showing them inline would be noise.
  const scrollEvents = events.filter(e => e.phase !== 'step.current');

  return (
    <div className="card mb-6">
      {/* Pinned current step — always visible at the top while in flight */}
      {currentStep && !done && (
        <div
          className="px-5 py-3 flex items-center gap-3"
          style={{
            borderBottom: '1px solid var(--glass-border)',
            background: 'linear-gradient(90deg, var(--accent-soft) 0%, transparent 100%)',
          }}
        >
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
            style={{ background: 'var(--accent)', boxShadow: '0 0 16px var(--accent-soft)' }}
          >
            <Loader2 className="w-4 h-4 text-white animate-spin" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-400">In progress</div>
            <div className="text-base font-semibold text-ink-strong tracking-tight truncate">
              {currentStep.message}
            </div>
          </div>
          {currentStep.detail?.progress_total !== undefined && (
            <div className="text-xs text-ink-400 font-mono shrink-0 tabular-nums">
              {String(currentStep.detail.progress_index ?? 0)} / {String(currentStep.detail.progress_total)}
            </div>
          )}
        </div>
      )}

      <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--glass-border)' }}>
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4" style={{ color: 'var(--accent)' }} />
          <h3 className="text-sm font-semibold text-ink-strong">Activity log</h3>
          {connected && !done && (
            <span className="text-xs text-emerald-500 inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              live
            </span>
          )}
          {done && (
            <span className="text-xs text-ink-400">finished</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-ink-400">
          {(() => {
            const { total, calls } = sumCost(events);
            if (calls === 0) return null;
            return (
              <span title={`Estimated cost across ${calls} Claude API call${calls === 1 ? '' : 's'} so far`}>
                <span className="text-ink-400">Claude spend: </span>
                <span className="font-semibold text-ink-strong font-mono">{formatUsd(total)}</span>
                <span className="text-ink-400"> · {calls} call{calls === 1 ? '' : 's'}</span>
              </span>
            );
          })()}
          <span>{scrollEvents.length} event{scrollEvents.length === 1 ? '' : 's'}</span>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="max-h-96 overflow-y-auto px-5 py-3 space-y-1.5 text-sm"
      >
        {scrollEvents.length === 0 && (
          <div className="text-ink-400 italic py-2">Waiting for events…</div>
        )}
        {scrollEvents.map((evt, i) => {
          const style = LEVEL_STYLES[evt.level];
          const Icon = style.icon;
          const isSpinner = evt.level === 'info';
          // Time delta from the previous event. Helps spot slow steps
          // without doing the subtraction in your head. Shown only on
          // events after the first; the first event has no predecessor
          // to compare against. Formatted as ms for sub-second deltas
          // and as Ns.Ms otherwise.
          const prevTs = i > 0 ? scrollEvents[i - 1].ts : null;
          const deltaMs = prevTs != null ? evt.ts - prevTs : null;
          const deltaLabel = formatDelta(deltaMs);
          return (
            <div
              key={`${evt.ts}-${i}`}
              className={`flex items-start gap-2.5 px-2.5 py-1.5 rounded-md ${style.bg}`}
            >
              <Icon
                className={`w-4 h-4 mt-0.5 shrink-0 ${style.color} ${isSpinner && !done ? 'animate-spin' : ''}`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-ink-400 font-mono">
                  {format(new Date(evt.ts), 'HH:mm:ss.SSS')}
                  {deltaLabel && <span className="text-axis-ash"> (+{deltaLabel})</span>}
                  {' · '}{evt.phase}
                </div>
                <div className="text-ink-strong break-words">{evt.message}</div>
                {evt.detail !== undefined && Object.keys(evt.detail).length > 0 && (
                  <DetailLine detail={evt.detail} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailLine({ detail }: { detail: Record<string, unknown> }) {
  const interesting: Array<{ k: string; v: unknown; isCost: boolean }> = [];
  const skip = new Set(['stack']);
  for (const [k, v] of Object.entries(detail)) {
    if (skip.has(k)) continue;
    if (v === null || v === undefined) continue;
    if (typeof v === 'object' && !Array.isArray(v)) continue;
    const isCost = (k === 'cost_usd' || k === 'total_usd') && typeof v === 'number';
    interesting.push({ k, v, isCost });
  }
  if (interesting.length === 0) return null;
  return (
    <div className="text-xs text-axis-ash mt-0.5 font-mono">
      {interesting.slice(0, 6).map(({ k, v, isCost }, i) => (
        <span key={i}>
          {i > 0 && ' · '}
          <span>{k}=</span>
          <span className={isCost ? 'text-emerald-300 font-semibold' : 'text-axis-ink'}>
            {isCost ? formatUsd(v as number) : Array.isArray(v) ? `[${v.length}]` : String(v)}
          </span>
        </span>
      ))}
    </div>
  );
}

/**
 * Format a millisecond delta between two events into a short label.
 * Returns null when the delta is null (first event) or zero (events
 * that share a timestamp — common when the worker fires multiple
 * progress events synchronously).
 *
 * Examples:
 *   86      → '86ms'
 *   1234    → '1.2s'
 *   65000   → '1m 5s'
 *
 * Designed for inline display alongside the event timestamp. The
 * format prioritizes glanceability: an operator scanning the
 * activity log should be able to see "ah, that step took 4 seconds"
 * without doing math.
 */
function formatDelta(ms: number | null): string | null {
  if (ms === null || ms === 0) return null;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60_000);
  const secs = Math.floor((ms % 60_000) / 1000);
  return `${mins}m ${secs}s`;
}

